<template>
  <div
    class="orders-page"
    v-loading.fullscreen.lock="importingOrders"
    element-loading-text="正在导入中..."
  >
    <div class="header-row">
      <div>
        <h2>订单管理</h2>
        <p>{{ orderPageDescription }}</p>
      </div>
      <div class="header-actions">
        <el-button v-if="canCreateOrder" plain :disabled="importingOrders" @click="downloadImportTemplate">下载导入模版</el-button>
        <el-button v-if="canCreateOrder" plain :loading="importingOrders" :disabled="importingOrders" @click="triggerImportOrders">批量导入订单</el-button>
        <el-button v-if="canExport" plain @click="exportCurrent">导出当前结果</el-button>
        <el-button v-if="canCreateOrder" type="primary" @click="openCreate">手工录入订单</el-button>
        <input ref="importFileInput" type="file" accept=".xlsx" style="display: none;" @change="handleImportFileChange" />
      </div>
    </div>

    <div class="brand-card filter-card">
      <div class="filter-card__header">
        <strong>查询条件</strong>
        <el-button size="mini" plain @click="filtersCollapsed = !filtersCollapsed">
          {{ filtersCollapsed ? "展开" : "收起" }}
        </el-button>
      </div>
      <el-collapse-transition>
        <div v-show="!filtersCollapsed" class="toolbar-row">
        <el-input v-model="filters.order_no" placeholder="订单号（精确）" clearable />
        <el-input v-model="filters.keyword" placeholder="收件人 / 上游单号" clearable />
        <el-input v-model="filters.recipient_phone" placeholder="收件人电话（精确）" clearable />
        <el-select v-model="filters.status" placeholder="订单状态" clearable>
          <el-option v-for="status in statuses" :key="status" :label="status" :value="status" />
        </el-select>
        <el-select v-model="filters.business_type" placeholder="业务类型" clearable filterable>
          <el-option v-for="item in businessTypeOptions" :key="item.id" :label="item.name" :value="item.name" />
        </el-select>
        <el-input v-model="filters.new_phone" placeholder="新开号码" clearable />
        <el-input v-model="filters.team_name" placeholder="归属团队" clearable />
        <el-select v-model="filters.courier_id" placeholder="配送员" clearable filterable>
          <el-option v-for="courier in couriers" :key="courier.id" :label="courier.name" :value="courier.id" />
        </el-select>
        <el-input v-model="filters.address_keyword" placeholder="收件地址" clearable />
        <el-select v-model="filters.source" placeholder="建单来源" clearable filterable>
          <el-option v-for="item in orderSourceOptions" :key="item.id" :label="displayOrderSource(item.name)" :value="item.name" />
        </el-select>
        <el-select v-model="filters.is_urgent" placeholder="是否加急" clearable>
          <el-option label="加急" :value="true" />
          <el-option label="非加急" :value="false" />
        </el-select>
        <el-select v-model="filters.is_published" placeholder="是否发布" clearable>
          <el-option label="已发布" :value="true" />
          <el-option label="未发布" :value="false" />
        </el-select>
        <el-select v-if="canFilterManager" v-model="filters.manager_id" placeholder="归属管理员" clearable filterable>
          <el-option v-for="leader in leaders" :key="leader.id" :label="leader.name" :value="leader.id" />
        </el-select>
        <el-date-picker
          v-model="filters.dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="yyyy-MM-dd"
        />
        <el-button type="primary" plain @click="handleQuery">查询</el-button>
        </div>
      </el-collapse-transition>
    </div>

    <div class="brand-card table-card">
      <div v-if="batchActionsVisible" class="batch-toolbar">
        <div class="batch-toolbar__left">
          <span>已选 {{ selectedRows.length }} 条</span>
        </div>
        <div class="batch-toolbar__actions">
          <el-button v-if="canBatchAssign" size="small" :disabled="!hasSelection" @click="openBatchAssign">批量派单</el-button>
          <el-button v-if="canBatchStatus" size="small" :disabled="!hasSelection" @click="openBatchStatus">批量改状态</el-button>
          <el-button v-if="canBatchPublish" size="small" :disabled="!hasSelection" @click="submitBatchPublish">批量发布</el-button>
          <el-button v-if="canBatchUrgent" size="small" :disabled="!hasSelection" @click="submitBatchUrgent(true)">批量标记加急</el-button>
          <el-button v-if="canBatchUrgent" size="small" :disabled="!hasSelection" @click="submitBatchUrgent(false)">批量取消加急</el-button>
          <el-button v-if="canBatchExport" size="small" :disabled="!hasSelection" @click="submitBatchExport">批量导出</el-button>
        </div>
      </div>

      <div class="table-scroll">
        <el-table
          ref="ordersTable"
          :data="orders"
          :height="ordersTableHeight"
          stripe
          @selection-change="handleSelectionChange"
        >
          <el-table-column type="selection" width="48" :selectable="isRowSelectable" />
          <el-table-column prop="status" label="状态" width="110">
            <template slot-scope="{ row }">
              <span class="status-badge" :class="statusClass(row.status)">{{ row.status || "-" }}</span>
            </template>
          </el-table-column>
          <el-table-column label="是否加急" width="100">
            <template slot-scope="{ row }">
              <span class="status-badge" :class="row.is_urgent ? 'urgent-badge--yes' : 'urgent-badge--no'">
                {{ row.is_urgent ? "加急" : "不加急" }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="配送短信" width="100">
            <template slot-scope="{ row }">
              <span class="status-badge" :class="row.delivery_sms_enabled ? 'sms-badge--yes' : 'sms-badge--no'">
                {{ row.delivery_sms_enabled ? "发送" : "不发送" }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="发布" width="100">
            <template slot-scope="{ row }">
              <span class="status-badge" :class="row.is_published ? 'published-badge--yes' : 'published-badge--no'">
                {{ row.is_published ? "已发布" : "未发布" }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="是否超时" width="120">
            <template slot-scope="{ row }">
              <span class="timeout-badge" :class="row.is_timeout ? 'timeout-badge--yes' : 'timeout-badge--no'">
                {{ row.is_timeout ? "已超时" : "未超时" }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="order_no" label="订单号" min-width="160" />
          <el-table-column prop="team_name" label="归属团队" min-width="120" />
          <el-table-column prop="business_type" label="业务类型" min-width="120" />
          <el-table-column prop="product_name" label="产品名称" min-width="140" />
          <el-table-column prop="promotion_plan" label="宣传方案" min-width="160" />
          <el-table-column prop="sales_name" label="归属业务员" min-width="120" />
          <el-table-column prop="manager_name" label="归属管理员" min-width="120" />
          <el-table-column prop="courier_name" label="归属配送员" min-width="120" />
          <el-table-column prop="recipient_name" label="收件人" width="100" />
          <el-table-column prop="recipient_phone" label="联系电话" min-width="150">
            <template slot-scope="{ row }">
              <span class="sensitive-cell">
                <span>{{ row.recipient_phone || "-" }}</span>
                <el-button
                  v-if="row.recipient_phone"
                  type="text"
                  icon="el-icon-view"
                  title="查看完整内容"
                  @click.stop="revealSensitive(row, 'recipient_phone')"
                />
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="province_name" label="省" min-width="110" />
          <el-table-column prop="city_name" label="市" min-width="110" />
          <el-table-column prop="district_name" label="区县" min-width="110" />
          <el-table-column prop="street_name" label="街道/乡镇" min-width="130" />
          <el-table-column prop="detail_address" label="详细地址" min-width="240">
            <template slot-scope="{ row }">
              <span class="sensitive-cell">
                <span>{{ row.detail_address || "-" }}</span>
                <el-button
                  v-if="row.detail_address"
                  type="text"
                  icon="el-icon-view"
                  title="查看完整内容"
                  @click.stop="revealSensitive(row, 'detail_address')"
                />
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="new_phone" label="新开号码" min-width="150">
            <template slot-scope="{ row }">
              <span class="sensitive-cell">
                <span>{{ row.new_phone || "-" }}</span>
                <el-button
                  v-if="row.new_phone"
                  type="text"
                  icon="el-icon-view"
                  title="查看完整内容"
                  @click.stop="revealSensitive(row, 'new_phone')"
                />
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="expected_delivery_date" label="期望配送时间" min-width="120" />
          <el-table-column prop="delivery_time_range" label="配送时间段" min-width="130" />
          <el-table-column prop="receivable_amount" label="应收金额" width="100" />
          <el-table-column prop="actual_amount" label="实收金额" width="100" />
          <el-table-column label="是否新开户" width="110">
            <template slot-scope="{ row }">{{ row.is_new_account ? "是" : "否" }}</template>
          </el-table-column>
          <el-table-column prop="source" label="建单来源" min-width="180">
            <template slot-scope="{ row }">{{ displayOrderSource(row.source, row.upstream_app_name) }}</template>
          </el-table-column>
          <el-table-column prop="upstream_order_no" label="上游单号" min-width="140" />
          <el-table-column prop="downstream_app_name" label="下游应用" min-width="140" />
          <el-table-column label="抢单距离" min-width="110">
            <template slot-scope="{ row }">{{ formatGrabDistance(row.grab_distance_meters) }}</template>
          </el-table-column>
          <el-table-column prop="submitted_review_at" label="提交待审核时间" min-width="180" />
          <el-table-column prop="completed_at" label="完成时间" min-width="180" />
          <el-table-column prop="failed_at" label="失败时间" min-width="180" />
          <el-table-column
            label="操作"
            :width="actionColumnWidth"
            fixed="right"
            class-name="action-column"
            label-class-name="action-column-header"
          >
            <template slot-scope="{ row }">
              <div class="action-cell">
                <el-button size="mini" type="info" plain @click="goDetail(row)">详情</el-button>
                <el-button v-if="canEdit(row)" size="mini" type="primary" plain @click="openEdit(row)">编辑</el-button>
                <el-button size="mini" type="success" plain @click="openMap(row)">查看位置</el-button>
                <el-button v-if="canViewLogs" size="mini" type="info" plain @click="openLogs(row)">日志</el-button>
                <el-button v-if="canAssign(row)" size="mini" type="warning" @click="openAssign(row)">{{ row.status === "失败" ? "改配送员" : "派单" }}</el-button>
                <el-button v-if="canChangeStatus(row)" size="mini" type="primary" @click="openStatus(row)">改状态</el-button>
                <el-dropdown v-if="hasMoreActions(row)" trigger="click" @command="(command) => handleMoreAction(command, row)">
                  <el-button size="mini" plain type="info">
                    更多<i class="el-icon-arrow-down el-icon--right"></i>
                  </el-button>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item v-if="canReview(row)" command="review_pass">审核通过</el-dropdown-item>
                    <el-dropdown-item v-if="canReview(row)" command="review_reject">驳回</el-dropdown-item>
                    <el-dropdown-item v-if="canUploadAttachment(row)" command="attachment">附件</el-dropdown-item>
                    <el-dropdown-item v-if="canDeleteOrder(row)" command="delete" class="danger-dropdown-item">删除</el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="pager-row">
        <el-pagination
          background
          layout="total, prev, pager, next, sizes"
          :total="total"
          :page-size="pager.page_size"
          :current-page="pager.page"
          :page-sizes="[10, 20, 50]"
          @current-change="onPageChange"
          @size-change="onSizeChange"
        />
      </div>
    </div>

    <el-dialog
      :title="createDialogTitle"
      :visible.sync="showCreate"
      width="980px"
      :close-on-click-modal="false"
      @close="resetCreateDialog"
    >
      <el-form :model="createForm" label-width="110px">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item :label="createLabel('归属业务员', 'sales_id')">
              <el-select
                v-if="currentUser.role !== 'sales'"
                v-model="createForm.sales_id"
                clearable
                filterable
                style="width: 100%;"
                placeholder="请选择归属业务员"
              >
                <el-option v-for="sales in salesUsers" :key="sales.id" :label="sales.name" :value="sales.id" />
              </el-select>
              <el-input v-else :value="currentUser.name || currentUser.username" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="createLabel('产品明细', 'product_id')">
              <div class="product-items-editor">
                <div v-for="(item, index) in createForm.product_items" :key="item.uid" class="product-item-row">
                  <el-select v-model="item.business_type" clearable filterable placeholder="业务类型" @change="handleProductItemBusinessTypeChange(index)">
                    <el-option v-for="option in businessTypeOptions" :key="option.id" :label="option.name" :value="option.name" />
                  </el-select>
                  <el-select v-model="item.product_id" clearable filterable placeholder="产品" :disabled="!item.business_type" @change="handleProductItemProductChange(index)">
                    <el-option v-for="option in getProductItemOptions(item)" :key="option.id" :label="option.name" :value="option.id" />
                  </el-select>
                  <el-switch v-model="item.is_new_account" active-text="新开户" @change="handleProductItemNewAccountChange(index)" />
                  <el-input v-if="item.is_new_account" v-model="item.new_phone" placeholder="新开号码" />
                  <el-input-number v-if="item.is_new_account" v-model="item.first_recharge_amount" :min="0" :precision="2" placeholder="首充金额" />
                  <el-button class="product-item-delete" icon="el-icon-delete" circle plain :disabled="createForm.product_items.length <= 1" @click="removeProductItem(index)" />
                </div>
                <el-button size="mini" icon="el-icon-plus" @click="addProductItem()">添加产品</el-button>
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="12"><el-form-item :label="createLabel('宣传方案', 'promotion_plan')"><el-input v-model="createForm.promotion_plan" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item :label="createLabel('收件人', 'recipient_name')"><el-input v-model="createForm.recipient_name" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item :label="createLabel('联系电话', 'recipient_phone')"><el-input v-model="createForm.recipient_phone" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item :label="createLabel('身份证号', 'id_card_no')"><el-input v-model="createForm.id_card_no" /></el-form-item></el-col>
          <el-col :span="8">
            <el-form-item :label="createLabel('收件地址', 'address')">
              <el-select v-model="createForm.province_id" clearable style="width: 100%;" @change="handleProvinceChange">
                <el-option v-for="item in provinces" :key="item.id" :label="item.name" :value="item.id" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="市">
              <el-select v-model="createForm.city_id" clearable style="width: 100%;" :disabled="!createForm.province_id" @change="handleCityChange">
                <el-option v-for="item in cities" :key="item.id" :label="item.name" :value="item.id" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="区县">
              <el-select v-model="createForm.district_id" clearable style="width: 100%;" :disabled="!createForm.city_id" @change="handleDistrictChange">
                <el-option v-for="item in districts" :key="item.id" :label="item.name" :value="item.id" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="街道/乡镇">
              <el-select v-model="createForm.street_id" clearable style="width: 100%;" :disabled="!createForm.district_id">
                <el-option v-for="item in streets" :key="item.id" :label="item.name" :value="item.id" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="createLabel('详细地址', 'detail_address')">
              <el-input
                v-model="createForm.detail_address"
                placeholder="可粘贴完整地址自动识别省市区县街道"
                @paste.native="handleDetailAddressPaste"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12"><el-form-item :label="createLabel('归属团队', 'team_name')"><el-input v-model="createForm.team_name" /></el-form-item></el-col>
          <el-col :span="12">
            <el-form-item :label="createLabel('期望配送时间', 'expected_delivery_date')">
              <el-date-picker v-model="createForm.expected_delivery_date" type="date" value-format="yyyy-MM-dd" style="width: 100%;" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="createLabel('配送时间', 'delivery_time_range')">
              <div class="delivery-time-field">
                <el-input v-model.trim="createForm.delivery_time_range" placeholder="例如 12:00 或 10:00-12:00" @blur="normalizeDeliveryTimeRange" />
                <div class="delivery-time-presets">
                  <el-button size="mini" plain @click="applyDeliveryPreset('08:00-12:00')">上午（08:00 - 12:00）</el-button>
                  <el-button size="mini" plain @click="applyDeliveryPreset('12:00-14:00')">中午（12:00 - 14:00）</el-button>
                  <el-button size="mini" plain @click="applyDeliveryPreset('14:00-18:00')">下午（14:00 - 18:00）</el-button>
                  <el-button size="mini" plain @click="applyDeliveryPreset('18:00-21:00')">晚上（18:00 - 21:00）</el-button>
                </div>
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="12"><el-form-item :label="createLabel('应收金额', 'receivable_amount')"><el-input-number v-model="createForm.receivable_amount" :min="0" :precision="2" style="width: 100%;" /></el-form-item></el-col>
          <el-col :span="12">
            <el-form-item :label="createLabel('建单来源', 'channel')">
              <el-select v-model="createForm.channel" style="width: 100%;" clearable filterable>
                <el-option v-for="item in orderSourceOptions" :key="item.id" :label="displayOrderSource(item.name)" :value="item.name" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12"><el-form-item :label="createLabel('上游单号', 'upstream_order_no')"><el-input v-model="createForm.upstream_order_no" /></el-form-item></el-col>
          <el-col :span="12">
            <el-form-item label="下游应用">
              <el-select v-model="createForm.downstream_app_id" clearable filterable style="width: 100%;" placeholder="不下发给下游">
                <el-option v-for="item in downstreamAppOptions" :key="item.id" :label="item.name" :value="item.id" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12"><el-form-item :label="createLabel('是否加急', 'is_urgent')"><el-switch v-model="createForm.is_urgent" /></el-form-item></el-col>
          <el-col v-if="canUseDeliverySms" :span="12"><el-form-item label="配送短信"><el-switch v-model="createForm.delivery_sms_enabled" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item label="发布"><el-switch v-model="createForm.is_published" /></el-form-item></el-col>
          <el-col :span="24"><el-form-item :label="createLabel('备注', 'remark')"><el-input v-model="createForm.remark" type="textarea" :rows="3" /></el-form-item></el-col>
          <el-col v-if="editingOrder && canEditCustomFields" :span="24">
            <div class="custom-field-section">
              <div class="custom-field-section__title">管理员自定义字段</div>
              <el-row :gutter="16">
                <el-col :span="12">
                  <el-form-item label="字段1文案">
                    <el-input v-model="createForm.custom_field1_label" placeholder="例如：内部标记" />
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="字段1值">
                    <el-input v-model="createForm.custom_field1_value" placeholder="请输入字段1值" />
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="字段2文案">
                    <el-input v-model="createForm.custom_field2_label" placeholder="例如：回访备注" />
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="字段2值">
                    <el-input v-model="createForm.custom_field2_value" placeholder="请输入字段2值" />
                  </el-form-item>
                </el-col>
              </el-row>
            </div>
          </el-col>
        </el-row>
      </el-form>
      <span slot="footer">
        <el-button @click="showCreate = false">取消</el-button>
        <el-button type="primary" @click="submitCreate">{{ editingOrder ? "保存" : "提交" }}</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="batchAssignMode ? '批量派单' : '派单'" :visible.sync="showAssign" width="480px" @close="resetAssignDialog">
      <el-form label-width="100px">
        <el-form-item label="分配类型">
          <el-radio-group v-model="assignForm.target_type">
            <el-radio label="courier">配送员</el-radio>
            <el-radio v-if="allowAssignSales" label="sales">业务员</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="assignForm.target_type === 'courier'" label="配送员">
          <el-select v-model="assignForm.courier_id" clearable filterable style="width: 100%;">
            <el-option
              v-for="courier in couriers"
              :key="courier.id"
              :label="`${courier.name} (${grabModeText(courier.grab_mode)})`"
              :value="courier.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item v-else label="业务员">
          <el-select v-model="assignForm.sales_id" clearable filterable style="width: 100%;">
            <el-option v-for="sales in salesUsers" :key="sales.id" :label="salesUserLabel(sales)" :value="sales.id" />
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="showAssign = false">取消</el-button>
        <el-button type="primary" @click="submitAssign">确认</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="batchStatusMode ? '批量修改状态' : '修改订单状态'" :visible.sync="showStatus" width="560px" @close="resetStatusDialog">
      <el-form :model="statusForm" label-width="110px">
        <el-form-item label="目标状态">
          <el-select v-model="statusForm.status" style="width: 100%;">
            <el-option v-for="status in statusOptions" :key="status" :label="status" :value="status" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="statusForm.status === '改约中'" label="改约时间">
          <el-date-picker v-model="statusForm.reschedule_time" type="datetime" value-format="yyyy-MM-ddTHH:mm:ss" />
        </el-form-item>
        <el-form-item v-if="statusForm.status === '改约中'" label="改约备注"><el-input v-model="statusForm.reschedule_remark" type="textarea" /></el-form-item>
        <el-form-item v-if="statusForm.status === '失败'" label="失败原因"><el-input v-model="statusForm.failure_remark" type="textarea" /></el-form-item>
        <el-form-item v-if="statusForm.status === '待审核'" label="完成备注"><el-input v-model="statusForm.completion_remark" type="textarea" /></el-form-item>
        <el-form-item v-if="statusForm.status === '待审核'" label="支付方式">
          <el-select v-model="statusForm.payment_type" clearable filterable style="width: 100%;" placeholder="请选择支付方式">
            <el-option v-for="item in paymentTypeOptions" :key="item.id" :label="item.name" :value="item.name" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="statusForm.status === '待审核'" label="实收金额"><el-input-number v-model="statusForm.actual_amount" :min="0" :precision="2" style="width: 100%;" /></el-form-item>
        <el-form-item v-if="statusForm.status === '待审核'" label="是否新开户"><el-switch v-model="statusForm.is_new_account" /></el-form-item>
        <el-form-item v-if="statusForm.status === '待审核' && statusForm.is_new_account" label="新开号码"><el-input v-model="statusForm.new_phone" /></el-form-item>
        <el-form-item v-if="statusForm.status === '待审核' && statusForm.is_new_account" label="首充金额"><el-input-number v-model="statusForm.first_recharge_amount" :min="0" :precision="2" style="width: 100%;" /></el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="showStatus = false">取消</el-button>
        <el-button type="primary" @click="submitStatus">确认</el-button>
      </span>
    </el-dialog>

    <el-dialog title="上传附件" :visible.sync="showAttachment" width="520px">
      <el-form :model="attachmentForm" label-width="100px">
        <el-form-item label="选择文件"><input type="file" @change="handleFileChange" /></el-form-item>
        <el-form-item label="类型">
          <el-select v-model="attachmentForm.file_type" style="width: 100%;">
            <el-option label="图片" value="image" />
            <el-option label="录音" value="audio" />
          </el-select>
        </el-form-item>
        <el-form-item label="文件名称"><el-input v-model="attachmentForm.file_name" /></el-form-item>
        <el-form-item label="过期时间"><el-date-picker v-model="attachmentForm.expire_at" type="datetime" value-format="yyyy-MM-ddTHH:mm:ss" /></el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="showAttachment = false">取消</el-button>
        <el-button type="primary" :loading="uploading" @click="submitAttachment">上传并保存</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="logDialogTitle" :visible.sync="showLogs" width="860px">
      <div v-if="logs.length" class="timeline-wrap">
        <el-timeline>
          <el-timeline-item
            v-for="item in orderedLogs"
            :key="item.id"
            :timestamp="item.created_at"
            placement="top"
          >
            <div class="timeline-card">
              <div class="timeline-head">
                <strong>{{ item.operator_name || "-" }}</strong>
                <span class="timeline-role">{{ operatorRoleLabel(item.operator_role) }}</span>
                <span class="timeline-action">{{ actionTypeLabel(item.action_type) }}</span>
              </div>
              <div class="timeline-content">{{ item.action_content || "-" }}</div>
            </div>
          </el-timeline-item>
        </el-timeline>
      </div>
      <el-empty v-else description="暂无日志" :image-size="80" />
    </el-dialog>

    <el-dialog title="订单导入结果" :visible.sync="showImportResult" width="720px">
      <div class="import-summary">
        成功 {{ importResult.success_count }} 条，失败 {{ importResult.failed_count }} 条
      </div>
      <el-table :data="importResult.results" max-height="460">
        <el-table-column prop="row_no" label="行号" width="80" />
        <el-table-column prop="order_no" label="订单号" width="180" />
        <el-table-column prop="success" label="结果" width="90">
          <template slot-scope="{ row }">{{ row.success ? "成功" : "失败" }}</template>
        </el-table-column>
        <el-table-column prop="message" label="说明" min-width="260" />
      </el-table>
      <span slot="footer">
        <el-button type="primary" @click="showImportResult = false">关闭</el-button>
      </span>
    </el-dialog>

    <el-dialog title="选择导出字段" :visible.sync="showExportFields" width="760px">
      <div class="export-field-toolbar">
        <span>默认导出全部字段，可取消不需要的字段。</span>
        <div>
          <el-button size="mini" plain @click="selectAllExportFields">全选</el-button>
          <el-button size="mini" plain @click="clearExportFields">清空</el-button>
        </div>
      </div>
      <el-checkbox-group v-model="selectedExportFields" class="export-field-grid">
        <el-checkbox v-for="item in exportFieldOptions" :key="item.key" :label="item.key">
          {{ item.label }}
        </el-checkbox>
      </el-checkbox-group>
      <span slot="footer">
        <el-button @click="showExportFields = false">取消</el-button>
        <el-button type="primary" @click="confirmExport">确认导出</el-button>
      </span>
    </el-dialog>

    <el-dialog title="选择导出方式" :visible.sync="showExportSecurity" width="460px" :close-on-click-modal="false">
      <el-radio-group v-model="exportSensitiveMode" class="export-mode-options">
        <el-radio label="masked">脱敏导出（无需验证码）</el-radio>
        <el-radio label="unmasked">不脱敏导出（需短信验证码）</el-radio>
      </el-radio-group>
      <div v-if="exportSensitiveMode === 'unmasked'" class="sms-verify-row">
        <p>验证码将发送到当前登录账号绑定手机号（尾号 {{ exportPhoneLast4 || '----' }}）。</p>
        <el-input v-model="exportSmsCode" maxlength="6" placeholder="请输入短信验证码" />
        <el-button :loading="exportSmsSending" @click="sendExportSmsCode">发送验证码</el-button>
      </div>
      <span slot="footer">
        <el-button @click="showExportSecurity = false">取消</el-button>
        <el-button type="primary" :loading="exporting" @click="submitExport">确认导出</el-button>
      </span>
    </el-dialog>

    <el-dialog title="订单地址定位" :visible.sync="showMapDialog" width="960px" top="6vh">
      <div v-if="selectedMapOrder" class="map-summary">
        <div class="map-summary-item">
          <span class="map-summary-label">订单号</span>
          <span>{{ selectedMapOrder.order_no || "-" }}</span>
        </div>
        <div class="map-summary-item">
          <span class="map-summary-label">收件地址</span>
          <span>{{ formatOrderAddress(selectedMapOrder) }}</span>
        </div>
        <div class="map-summary-item">
          <span class="map-summary-label">坐标</span>
          <span>{{ formatOrderLocation(selectedMapOrder) }}</span>
        </div>
      </div>
      <div v-if="mapUrl" class="map-box">
        <iframe :src="mapUrl" class="map-frame" frameborder="0"></iframe>
      </div>
      <div v-else class="empty-map">该订单暂无地址定位</div>
    </el-dialog>
  </div>
</template>

<script>
import {
  assignOrder,
  batchAssignOrders,
  batchChangeOrdersStatus,
  batchExportOrders,
  batchMarkOrdersUrgent,
  batchPublishOrders,
  changeStatus,
  createOrder,
  deleteOrder,
  downloadOrderImportTemplate,
  exportOrders,
  fetchAuditLogs,
  fetchBusinessTypes,
  fetchCities,
  fetchCouriers,
  fetchDistricts,
  fetchLeaders,
  fetchLocationSettings,
  fetchManualOrderFieldSettings,
  fetchOrders,
  fetchOrderSources,
  fetchPaymentTypes,
  fetchProducts,
  fetchProvinces,
  fetchStreets,
  fetchSystemPermissions,
  fetchSalesUsers,
  fetchUpstreamApps,
  importOrders,
  reviewOrder,
  sendSensitiveExportSms,
  uploadAttachment,
  uploadFile,
  updateOrder,
  verifySensitiveExportSms,
} from "../api/orders";
import http from "../api/http";
import { getStoredUser, hasRole, roleLabel } from "../utils/auth";
import { getErrorMessage } from "../utils/errors";

const emptyProductItem = () => ({
  uid: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  business_type: "",
  product_id: null,
  product_name: "",
  is_new_account: false,
  new_phone: "",
  first_recharge_amount: null,
  product_options: [],
});

const emptyCreateForm = () => ({
  sales_id: null,
  product_id: null,
  product_name: "",
  promotion_plan: "",
  recipient_name: "",
  recipient_phone: "",
  card_holder_name: "",
  card_holder_phone: "",
  id_card_no: "",
  province_id: null,
  city_id: null,
  district_id: null,
  street_id: null,
  street_no: "",
  detail_address: "",
  city: "",
  supplier_name: "",
  business_type: "",
  team_name: "",
  is_urgent: false,
  delivery_sms_enabled: false,
  is_published: false,
  expected_delivery_date: "",
  delivery_time_range: "",
  receivable_amount: null,
  is_new_account: false,
  new_phone: "",
  first_recharge_amount: null,
  custom_field1_label: "",
  custom_field1_value: "",
  custom_field2_label: "",
  custom_field2_value: "",
  remark: "",
  source: "manual",
  channel: "",
  upstream_order_no: "",
  downstream_app_id: null,
  product_items: [emptyProductItem()],
});

const normalizePhoneWhitespace = (value) => String(value || "").replace(/\s+/g, "");

const emptyStatusForm = () => ({
  status: "配送中",
  reschedule_time: null,
  reschedule_remark: "",
  failure_remark: "",
  completion_remark: "",
  payment_type: "",
  actual_amount: null,
  is_new_account: false,
  new_phone: "",
  first_recharge_amount: null,
});

const emptyAttachmentForm = () => ({
  file_name: "",
  file_url: "",
  file_type: "image",
  expire_at: "",
});

const EXPORT_FIELD_OPTIONS = [
  { key: "order_no", label: "订单号" },
  { key: "promotion_plan", label: "宣传方案" },
  { key: "product_name", label: "产品名称" },
  { key: "product_remark", label: "产品备注" },
  { key: "recipient_name", label: "收件人" },
  { key: "recipient_phone", label: "联系电话" },
  { key: "card_holder_name", label: "办卡人" },
  { key: "card_holder_phone", label: "办卡人手机号" },
  { key: "id_card_no", label: "身份证号" },
  { key: "province_name", label: "省" },
  { key: "city_name", label: "市" },
  { key: "district_name", label: "区县" },
  { key: "street_name", label: "街道/乡镇" },
  { key: "street_no", label: "街道号码" },
  { key: "detail_address", label: "详细地址" },
  { key: "full_address", label: "完整地址" },
  { key: "business_type", label: "业务类型" },
  { key: "payment_type", label: "支付方式" },
  { key: "is_urgent", label: "是否加急" },
  { key: "delivery_sms_enabled", label: "配送短信" },
  { key: "is_published", label: "发布" },
  { key: "team_name", label: "归属团队" },
  { key: "status", label: "状态" },
  { key: "expected_delivery_date", label: "期望配送时间" },
  { key: "delivery_time_range", label: "配送时间段" },
  { key: "receivable_amount", label: "应收金额" },
  { key: "actual_amount", label: "实收金额" },
  { key: "is_new_account", label: "是否新开户" },
  { key: "new_phone", label: "新开号码" },
  { key: "first_recharge_amount", label: "首充金额" },
  { key: "source", label: "建单来源" },
  { key: "upstream_order_no", label: "上游单号" },
  { key: "upstream_app_name", label: "上游应用" },
  { key: "downstream_app_name", label: "下游应用" },
  { key: "created_by_name", label: "建单人" },
  { key: "manager_name", label: "归属管理员" },
  { key: "sales_name", label: "归属业务员" },
  { key: "courier_name", label: "归属配送员" },
  { key: "grab_distance_meters", label: "抢单距离" },
  { key: "submitted_review_at", label: "提交待审核时间" },
  { key: "completed_at", label: "完成时间" },
  { key: "failed_at", label: "失败时间" },
  { key: "reschedule_time", label: "改约时间" },
  { key: "remark", label: "订单备注" },
  { key: "reschedule_remark", label: "改约备注" },
  { key: "failure_remark", label: "失败原因" },
  { key: "completion_remark", label: "完成备注" },
  { key: "custom_field1_label", label: "自定义字段1名称" },
  { key: "custom_field1_value", label: "自定义字段1值" },
  { key: "custom_field2_label", label: "自定义字段2名称" },
  { key: "custom_field2_value", label: "自定义字段2值" },
  { key: "created_at", label: "创建时间" },
  { key: "updated_at", label: "更新时间" },
];

export default {
  data() {
    return {
      currentUser: getStoredUser(),
      filters: {
        order_no: "",
        keyword: "",
        recipient_phone: "",
        status: "",
        is_urgent: "",
        is_published: "",
        source: "",
        business_type: "",
        new_phone: "",
        team_name: "",
        courier_id: "",
        address_keyword: "",
        manager_id: "",
        dateRange: [],
      },
      statuses: ["待分配", "待配送", "配送中", "改约中", "待审核", "已完成", "失败"],
      orders: [],
      selectedRows: [],
      couriers: [],
      salesUsers: [],
      leaders: [],
      businessTypeOptions: [],
      createProductOptions: [],
      productOptionsByBusinessType: {},
      orderSourceOptions: [],
      paymentTypeOptions: [],
      downstreamAppOptions: [],
      manualFieldSettings: [],
      manualRequiredFields: [],
      provinces: [],
      cities: [],
      districts: [],
      streets: [],
      logs: [],
      importResult: { success_count: 0, failed_count: 0, results: [] },
      total: 0,
      pager: { page: 1, page_size: 10 },
      filtersCollapsed: false,
      ordersTableHeight: 680,
      uploading: false,
      importingOrders: false,
      selectedFile: null,
      showCreate: false,
      showAssign: false,
      showStatus: false,
      showAttachment: false,
      showLogs: false,
      showImportResult: false,
      showExportFields: false,
      showExportSecurity: false,
      showMapDialog: false,
      editingOrder: null,
      selectedOrder: null,
      selectedMapOrder: null,
      locationSettings: {},
      canUseDeliverySms: false,
      mapUrl: "",
      exportMode: "current",
      exportFieldOptions: EXPORT_FIELD_OPTIONS,
      selectedExportFields: EXPORT_FIELD_OPTIONS.map((item) => item.key),
      exportSensitiveMode: "masked",
      exportSmsCode: "",
      exportVerifyToken: "",
      exportPhoneLast4: "",
      exportSmsSending: false,
      exporting: false,
      batchAssignMode: false,
      batchStatusMode: false,
      assignForm: { target_type: "courier", courier_id: null, sales_id: null },
      createForm: emptyCreateForm(),
      statusForm: emptyStatusForm(),
      attachmentForm: emptyAttachmentForm(),
    };
  },
  computed: {
    hasSelection() {
      return this.selectedRows.length > 0;
    },
    canCreateOrder() {
      return hasRole(this.currentUser, ["super_admin", "admin", "sales"]);
    },
    canExport() {
      return hasRole(this.currentUser, ["super_admin", "admin"]);
    },
    orderPageDescription() {
      return this.canExport ? "按业务口径筛选、派单、审核和导出订单。" : "按业务口径筛选、派单和审核订单。";
    },
    canAssignOrders() {
      return hasRole(this.currentUser, ["super_admin", "admin"]);
    },
    canFilterManager() {
      return hasRole(this.currentUser, ["super_admin", "admin"]);
    },
    canViewLogs() {
      return hasRole(this.currentUser, ["super_admin", "admin", "sales"]);
    },
    canBatchAssign() {
      return hasRole(this.currentUser, ["super_admin", "admin"]);
    },
    canBatchStatus() {
      return hasRole(this.currentUser, ["super_admin", "admin", "sales"]);
    },
    canBatchUrgent() {
      return hasRole(this.currentUser, ["super_admin", "admin", "sales"]);
    },
    canBatchPublish() {
      return hasRole(this.currentUser, ["super_admin", "admin", "sales"]);
    },
    canBatchExport() {
      return hasRole(this.currentUser, ["super_admin", "admin"]);
    },
    canEditCustomFields() {
      return hasRole(this.currentUser, ["super_admin", "admin"]);
    },
    batchActionsVisible() {
      return this.canBatchAssign || this.canBatchStatus || this.canBatchUrgent || this.canBatchPublish || this.canBatchExport;
    },
    actionColumnWidth() {
      return 378;
    },
    createDialogTitle() {
      return this.editingOrder ? `编辑订单 - ${this.editingOrder.order_no || ""}` : "手工录单";
    },
    logDialogTitle() {
      if (!this.selectedOrder) return "订单流程日志";
      return `订单流程日志 - ${this.selectedOrder.order_no || ""}`;
    },
    orderedLogs() {
      return [...this.logs].sort((a, b) => {
        const left = new Date(a.created_at).getTime();
        const right = new Date(b.created_at).getTime();
        return Number.isNaN(left) || Number.isNaN(right) ? 0 : left - right;
      });
    },
    allowAssignSales() {
      if (this.batchAssignMode) {
        return this.selectedRows.every((row) => row.status === "待分配");
      }
      return this.selectedOrder && this.selectedOrder.status === "待分配";
    },
    statusOptions() {
      if (hasRole(this.currentUser, ["super_admin", "admin", "sales"])) {
        return [...this.statuses, "已完成"];
      }
      return [];
    },
  },
  mounted() {
    this.loadOrders();
    this.loadProvinces();
    this.loadBusinessTypes();
    this.loadOrderSources();
    this.loadPaymentTypes();
    this.loadDownstreamApps();
    this.loadManualFieldSettings();
    this.loadLocationSettings();
    this.loadSystemPermissions();
    if (this.canAssignOrders) {
      this.loadCouriers();
      this.loadSalesUsers();
    }
    if (this.canFilterManager) {
      this.loadLeaders();
    }
  },
  methods: {
    async loadLocationSettings() {
      try {
        const { data } = await fetchLocationSettings();
        this.locationSettings = data || {};
      } catch (error) {
        this.locationSettings = {};
      }
    },
    async loadSystemPermissions() {
      try {
        const { data } = await fetchSystemPermissions();
        this.canUseDeliverySms = !!data.can_use_delivery_sms;
      } catch (error) {
        this.canUseDeliverySms = false;
      }
    },
    async loadDownstreamApps() {
      const { data } = await fetchUpstreamApps({ app_type: "下游", is_active: true, page: 1, page_size: 100 });
      this.downstreamAppOptions = data.items || [];
    },
    displayOrderSource(source, upstreamAppName = "") {
      if (source === "api") {
        return upstreamAppName ? `api接入：${upstreamAppName}` : "api接入";
      }
      if (source === "manual") {
        return "手工录入";
      }
      return source || "-";
    },
    statusClass(status) {
      const map = {
        待分配: "status-badge--wait-assign",
        待配送: "status-badge--wait-delivery",
        配送中: "status-badge--in-delivery",
        改约中: "status-badge--rescheduled",
        待审核: "status-badge--pending-review",
        已完成: "status-badge--completed",
        失败: "status-badge--failed",
      };
      return map[status] || "status-badge--default";
    },
    actionTypeLabel(type) {
      const map = {
        CREATE_ORDER: "创建订单",
        UPDATE_ORDER: "编辑订单",
        ASSIGN_ORDER: "派单/转派",
        GRAB_ORDER: "抢单",
        CHANGE_STATUS: "修改状态",
        REVIEW_PASS: "审核通过",
        REVIEW_REJECT: "审核驳回",
        DELETE_ORDER: "删除订单",
        VIEW_ORDER_DETAIL: "查看详情",
        VIEW_SENSITIVE: "查看敏感字段",
        CALL_PHONE: "拨打电话",
        CALL_RECIPIENT: "拨打收件人电话",
        UPLOAD_ATTACHMENT: "上传附件",
        BATCH_MARK_URGENT: "批量修改加急",
      };
      return map[type] || type || "-";
    },
    operatorRoleLabel(role) {
      return roleLabel(role);
    },
    grabModeText(mode) {
      if (mode === "assign_only") return "只分单";
      if (mode === "grab_only") return "只抢单";
      if (mode === "assign_and_grab") return "分单+抢单";
      return mode || "-";
    },
    formatGrabDistance(value) {
      if (value === null || value === undefined || value === "") return "未记录";
      const meters = Number(value);
      if (!Number.isFinite(meters)) return "未记录";
      if (meters >= 1000) return `${(meters / 1000).toFixed(1)}km`;
      return `${meters}m`;
    },
    buildListParams() {
      return {
        order_no: this.filters.order_no || undefined,
        keyword: this.filters.keyword || undefined,
        recipient_phone: this.filters.recipient_phone || undefined,
        status: this.filters.status || undefined,
        is_urgent: this.filters.is_urgent === "" ? undefined : this.filters.is_urgent,
        is_published: this.filters.is_published === "" ? undefined : this.filters.is_published,
        source: this.filters.source || undefined,
        business_type: this.filters.business_type || undefined,
        new_phone: this.filters.new_phone || undefined,
        team_name: this.filters.team_name || undefined,
        courier_id: this.filters.courier_id || undefined,
        address_keyword: this.filters.address_keyword || undefined,
        manager_id: this.filters.manager_id || undefined,
        created_start: this.filters.dateRange[0],
        created_end: this.filters.dateRange[1],
      };
    },
    async loadOrders() {
      const params = {
        ...this.buildListParams(),
        page: this.pager.page,
        page_size: this.pager.page_size,
      };
      const { data } = await fetchOrders(params);
      this.orders = data.items || [];
      this.total = data.total || 0;
      this.clearSelection();
      this.$nextTick(() => {
        this.$refs.ordersTable?.doLayout();
      });
    },
    async loadBusinessTypes() {
      const { data } = await fetchBusinessTypes({ page: 1, page_size: 1000, is_active: true });
      this.businessTypeOptions = data.items || [];
    },
    async loadProductsForBusinessType(businessTypeName) {
      const businessType = this.businessTypeOptions.find((item) => item.name === businessTypeName);
      if (!businessType) {
        this.createProductOptions = [];
        return;
      }
      const { data } = await fetchProducts({ page: 1, page_size: 1000, is_active: true, business_type_id: businessType.id });
      this.createProductOptions = data.items || [];
    },
    async loadProductItemOptions(item) {
      if (!item || !item.business_type) {
        if (item) item.product_options = [];
        return [];
      }
      if (this.productOptionsByBusinessType[item.business_type]) {
        item.product_options = this.productOptionsByBusinessType[item.business_type];
        return item.product_options;
      }
      const businessType = this.businessTypeOptions.find((option) => option.name === item.business_type);
      if (!businessType) {
        item.product_options = [];
        return [];
      }
      const { data } = await fetchProducts({ page: 1, page_size: 1000, is_active: true, business_type_id: businessType.id });
      const options = data.items || [];
      this.$set(this.productOptionsByBusinessType, item.business_type, options);
      item.product_options = options;
      return options;
    },
    getProductItemOptions(item) {
      return item.product_options || this.productOptionsByBusinessType[item.business_type] || [];
    },
    async handleProductItemBusinessTypeChange(index) {
      const item = this.createForm.product_items[index];
      if (!item) return;
      item.product_id = null;
      item.product_name = "";
      await this.loadProductItemOptions(item);
    },
    handleProductItemProductChange(index) {
      const item = this.createForm.product_items[index];
      if (!item) return;
      const product = this.getProductItemOptions(item).find((option) => option.id === item.product_id);
      item.product_name = product ? product.name : "";
    },
    handleProductItemNewAccountChange(index) {
      const item = this.createForm.product_items[index];
      if (!item || item.is_new_account) return;
      item.new_phone = "";
      item.first_recharge_amount = null;
    },
    addProductItem(seed = {}) {
      const item = { ...emptyProductItem(), ...seed };
      this.createForm.product_items.push(item);
      this.loadProductItemOptions(item);
    },
    removeProductItem(index) {
      if (this.createForm.product_items.length <= 1) return;
      this.createForm.product_items.splice(index, 1);
    },
    buildLegacyProductItems(row) {
      const sourceItems = Array.isArray(row.product_items) && row.product_items.length
        ? row.product_items
        : [{
            business_type: row.business_type || "",
            product_id: row.product_id || null,
            product_name: row.product_name || "",
            is_new_account: !!row.is_new_account,
            new_phone: row.new_phone || "",
            first_recharge_amount: row.first_recharge_amount,
          }];
      return sourceItems.map((item) => ({
        ...emptyProductItem(),
        business_type: item.business_type || "",
        product_id: item.product_id || null,
        product_name: item.product_name || "",
        is_new_account: !!item.is_new_account,
        new_phone: item.new_phone || "",
        first_recharge_amount: item.first_recharge_amount,
      }));
    },
    async hydrateProductItems(items) {
      await Promise.all((items || []).map(async (item) => {
        const options = await this.loadProductItemOptions(item);
        if (item.product_id && !options.some((option) => option.id === item.product_id)) {
          item.product_options = [
            ...options,
            { id: item.product_id, name: item.product_name, business_type_name: item.business_type },
          ];
        }
      }));
    },
    normalizeProductItemsForSubmit() {
      const productItems = (this.createForm.product_items || []).map((item) => {
        const normalized = {
          business_type: (item.business_type || "").trim(),
          product_id: item.product_id || null,
          product_name: (item.product_name || "").trim(),
          is_new_account: !!item.is_new_account,
          new_phone: "",
          first_recharge_amount: null,
        };
        if (normalized.product_id && !normalized.product_name) {
          const product = this.getProductItemOptions(item).find((option) => option.id === normalized.product_id);
          normalized.product_name = product ? product.name : "";
        }
        if (normalized.is_new_account) {
          normalized.new_phone = normalizePhoneWhitespace(item.new_phone);
          normalized.first_recharge_amount = item.first_recharge_amount;
        }
        return normalized;
      });
      const filled = productItems.filter((item) => item.business_type || item.product_id || item.product_name);
      if (!filled.length) {
        this.$message.warning("请至少添加一个产品");
        return null;
      }
      for (let index = 0; index < filled.length; index += 1) {
        const item = filled[index];
        if (!item.business_type) {
          this.$message.warning(`请填写第 ${index + 1} 个产品的业务类型`);
          return null;
        }
        if (!item.product_id && !item.product_name) {
          this.$message.warning(`请选择第 ${index + 1} 个产品`);
          return null;
        }
        if (item.is_new_account) {
          if (!item.new_phone) {
            this.$message.warning(`请填写第 ${index + 1} 个产品的新开号码`);
            return null;
          }
          if (item.first_recharge_amount === null || item.first_recharge_amount === undefined || item.first_recharge_amount === "") {
            this.$message.warning(`请填写第 ${index + 1} 个产品的首充金额`);
            return null;
          }
          item.first_recharge_amount = Number(item.first_recharge_amount);
          if (Number.isNaN(item.first_recharge_amount) || item.first_recharge_amount < 0) {
            this.$message.warning(`请填写第 ${index + 1} 个产品的合法首充金额`);
            return null;
          }
        }
      }
      this.createForm.product_items = filled.map((item) => ({ ...emptyProductItem(), ...item }));
      this.syncLegacyProductFields(filled);
      return filled;
    },
    syncLegacyProductFields(productItems) {
      const items = productItems || [];
      const first = items[0] || {};
      const newAccountItem = items.find((item) => item.is_new_account) || {};
      this.createForm.product_id = first.product_id || null;
      this.createForm.product_name = items.map((item) => item.product_name).filter(Boolean).join("、");
      this.createForm.business_type = [...new Set(items.map((item) => item.business_type).filter(Boolean))].join("、");
      this.createForm.is_new_account = items.some((item) => item.is_new_account);
      this.createForm.new_phone = newAccountItem.new_phone || "";
      this.createForm.first_recharge_amount = newAccountItem.first_recharge_amount ?? null;
    },
    async handleCreateBusinessTypeChange() {
      this.createForm.product_id = null;
      this.createForm.product_name = "";
      this.createForm.promotion_plan = "";
      await this.loadProductsForBusinessType(this.createForm.business_type);
    },
    handleCreateProductChange(productId) {
      const product = this.createProductOptions.find((item) => item.id === productId);
      this.createForm.product_name = product ? product.name : "";
    },
    async loadOrderSources() {
      const { data } = await fetchOrderSources({ page: 1, page_size: 1000, is_active: true });
      this.orderSourceOptions = data.items || [];
    },
    async loadPaymentTypes() {
      const { data } = await fetchPaymentTypes({ page: 1, page_size: 1000, is_active: true });
      this.paymentTypeOptions = data.items || [];
    },
    async loadManualFieldSettings() {
      const { data } = await fetchManualOrderFieldSettings();
      this.manualFieldSettings = data.items || [];
      this.manualRequiredFields = this.manualFieldSettings.filter((item) => item.is_required).map((item) => item.field_key);
    },
    isManualFieldRequired(fieldKey) {
      return this.manualRequiredFields.includes(fieldKey);
    },
    createLabel(label, fieldKey) {
      return this.isManualFieldRequired(fieldKey) ? `${label} *` : label;
    },
    normalizeDeliveryTimeRange() {
      const value = this.getDeliveryTimeRangeValue(false);
      if (value !== null) {
        this.createForm.delivery_time_range = value;
      }
    },
    applyDeliveryPreset(value) {
      this.createForm.delivery_time_range = value;
    },
    getDeliveryTimeRangeValue() {
      const value = String(this.createForm.delivery_time_range || "").trim();
      if (!value) {
        return "";
      }
      const exact = value.match(/^(\d{1,2}):(\d{2})$/);
      if (exact) {
        const hour = Number(exact[1]);
        const minute = Number(exact[2]);
        if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
          return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
        }
        this.$message.warning("请输入合法的配送时间");
        return null;
      }
      const range = value.match(/^(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})$/);
      if (!range) {
        this.$message.warning("请输入合法的配送时间，如 12:00 或 10:00-12:00");
        return null;
      }
      const startHour = Number(range[1]);
      const startMinute = Number(range[2]);
      const endHour = Number(range[3]);
      const endMinute = Number(range[4]);
      if (startHour < 0 || startHour > 23 || endHour < 0 || endHour > 23 || startMinute < 0 || startMinute > 59 || endMinute < 0 || endMinute > 59) {
        this.$message.warning("请输入合法的配送时间");
        return null;
      }
      if (startHour * 60 + startMinute >= endHour * 60 + endMinute) {
        this.$message.warning("配送时间段的结束时间需要晚于开始时间");
        return null;
      }
      return `${String(startHour).padStart(2, "0")}:${String(startMinute).padStart(2, "0")}-${String(endHour).padStart(2, "0")}:${String(endMinute).padStart(2, "0")}`;
    },
    validateCreateForm() {
      this.createForm.recipient_phone = normalizePhoneWhitespace(this.createForm.recipient_phone);
      const deliveryTimeRangeValue = this.getDeliveryTimeRangeValue();
      if (deliveryTimeRangeValue === null) {
        return false;
      }
      this.createForm.delivery_time_range = deliveryTimeRangeValue;
      const productItems = this.normalizeProductItemsForSubmit();
      if (!productItems) return false;
      const required = this.manualRequiredFields || [];
      const missing = [];
      const textFields = {
        sales_id: "归属业务员",
        product_id: "产品名称",
        product_name: "产品名称",
        business_type: "业务类型",
        recipient_name: "收件人",
        recipient_phone: "联系电话",
        id_card_no: "身份证号",
        detail_address: "详细地址",
        team_name: "归属团队",
        expected_delivery_date: "期望配送时间",
        delivery_time_range: "配送时间段",
        receivable_amount: "应收金额",
        is_new_account: "是否新开户",
        new_phone: "新开号码",
        first_recharge_amount: "首充金额",
        channel: "建单来源",
        source: "建单方式",
        upstream_order_no: "上游单号",
        is_urgent: "是否加急",
        remark: "备注",
      };
      required.forEach((fieldKey) => {
        if (fieldKey === "address") {
          if (!this.createForm.province_id || !this.createForm.city_id) {
            missing.push("收件地址");
          }
          return;
        }
        const value = this.createForm[fieldKey];
        if (value === null || value === undefined || (typeof value === "string" && !value.trim())) {
          missing.push(textFields[fieldKey] || fieldKey);
        }
      });
      if (missing.length) {
        this.$message.warning(`请先填写必填项：${missing.join("、")}`);
        return false;
      }
      return true;
    },
    async loadProvinces() {
      const { data } = await fetchProvinces();
      this.provinces = data || [];
    },
    async loadCouriers() {
      const { data } = await fetchCouriers();
      this.couriers = data || [];
    },
    async loadSalesUsers() {
      const { data } = await fetchSalesUsers();
      this.salesUsers = data || [];
    },
    salesUserLabel(user) {
      const roleText = user.role === "admin" ? "管理员" : "业务员";
      return user.phone ? `${user.name}（${roleText} / ${user.phone}）` : `${user.name}（${roleText}）`;
    },
    async loadLeaders() {
      const { data } = await fetchLeaders();
      this.leaders = data || [];
    },
    async handleProvinceChange() {
      this.createForm.city_id = null;
      this.createForm.district_id = null;
      this.createForm.street_id = null;
      this.cities = [];
      this.districts = [];
      this.streets = [];
      if (!this.createForm.province_id) return;
      const { data } = await fetchCities(this.createForm.province_id);
      this.cities = data || [];
    },
    async handleCityChange() {
      this.createForm.district_id = null;
      this.createForm.street_id = null;
      this.districts = [];
      this.streets = [];
      if (!this.createForm.city_id) return;
      const { data } = await fetchDistricts(this.createForm.city_id);
      this.districts = data || [];
    },
    async handleDistrictChange() {
      this.createForm.street_id = null;
      this.streets = [];
      if (!this.createForm.district_id) return;
      const { data } = await fetchStreets(this.createForm.district_id);
      this.streets = data || [];
    },
    async loadAddressOptionsForForm(row) {
      this.cities = [];
      this.districts = [];
      this.streets = [];
      if (row.province_id) {
        const { data } = await fetchCities(row.province_id);
        this.cities = data || [];
      }
      if (row.city_id) {
        const { data } = await fetchDistricts(row.city_id);
        this.districts = data || [];
      }
      if (row.district_id) {
        const { data } = await fetchStreets(row.district_id);
        this.streets = data || [];
      }
    },
    async handleDetailAddressPaste(event) {
      const clipboardData = event.clipboardData || window.clipboardData;
      const pastedText = (clipboardData && clipboardData.getData("text")) || "";
      const address = pastedText.trim();
      if (!address) return;
      event.preventDefault();
      this.createForm.detail_address = address;
      await this.fillAddressFromPastedText(address);
    },
    async fillAddressFromPastedText(address) {
      if (!this.provinces.length) {
        await this.loadProvinces();
      }
      const matched = [];
      const province = this.findRegionInAddress(address, this.provinces);
      if (!province) {
        this.$message.warning("未能识别省份，请手动选择地区信息");
        return;
      }

      this.createForm.province_id = province.id;
      matched.push(province);
      this.createForm.city_id = null;
      this.createForm.district_id = null;
      this.createForm.street_id = null;
      this.cities = [];
      this.districts = [];
      this.streets = [];

      const { data: cities } = await fetchCities(province.id);
      this.cities = cities || [];
      const city = this.findRegionInAddress(address, this.cities);
      if (!city) {
        this.createForm.detail_address = this.removeMatchedRegionsFromAddress(address, matched);
        this.$message.warning("已识别省份，市/区县/街道请手动选择");
        return;
      }

      this.createForm.city_id = city.id;
      matched.push(city);
      const { data: districts } = await fetchDistricts(city.id);
      this.districts = districts || [];
      const district = this.findRegionInAddress(address, this.districts);
      if (!district) {
        this.createForm.detail_address = this.removeMatchedRegionsFromAddress(address, matched);
        this.$message.warning("已识别省市，区县/街道请手动选择");
        return;
      }

      this.createForm.district_id = district.id;
      matched.push(district);
      const { data: streets } = await fetchStreets(district.id);
      this.streets = streets || [];
      const street = this.findRegionInAddress(address, this.streets);
      if (street) {
        this.createForm.street_id = street.id;
        matched.push(street);
      }

      this.createForm.detail_address = this.removeMatchedRegionsFromAddress(address, matched);
      this.$message.success(street ? "已识别并填充省市区县街道" : "已识别省市区县，街道请手动选择");
    },
    findRegionInAddress(address, regions) {
      const compactAddress = this.compactAddressText(address);
      const candidates = (regions || [])
        .map((region) => {
          const aliases = this.regionNameAliases(region.name);
          const matchedAlias = aliases.find((alias) => compactAddress.includes(alias));
          return matchedAlias ? { region, alias: matchedAlias, index: compactAddress.indexOf(matchedAlias) } : null;
        })
        .filter(Boolean)
        .sort((left, right) => {
          if (left.index !== right.index) return left.index - right.index;
          return right.alias.length - left.alias.length;
        });
      return candidates.length ? candidates[0].region : null;
    },
    removeMatchedRegionsFromAddress(address, regions) {
      let result = address.trim();
      (regions || []).forEach((region) => {
        const aliases = this.regionNameAliases(region.name).sort((left, right) => right.length - left.length);
        const alias = aliases.find((item) => item && result.includes(item));
        if (alias) {
          result = result.replace(alias, "");
        }
      });
      return result.replace(/^[\s,，、;；-]+/, "").trim() || "";
    },
    compactAddressText(value) {
      return String(value || "").replace(/\s+/g, "");
    },
    regionNameAliases(name) {
      const text = this.compactAddressText(name);
      if (!text) return [];
      const aliases = new Set([text]);
      const suffixes = [
        "特别行政区",
        "自治州",
        "自治县",
        "自治区",
        "地区",
        "街道",
        "省",
        "市",
        "区",
        "县",
        "镇",
        "乡",
      ];
      suffixes.forEach((suffix) => {
        if (text.endsWith(suffix) && text.length > suffix.length + 1) {
          aliases.add(text.slice(0, -suffix.length));
        }
      });
      return [...aliases].filter((item) => item.length >= 2);
    },
    handleQuery() {
      this.pager.page = 1;
      this.loadOrders();
    },
    onPageChange(page) {
      this.pager.page = page;
      this.loadOrders();
    },
    onSizeChange(size) {
      this.pager.page_size = size;
      this.pager.page = 1;
      this.loadOrders();
    },
    handleSelectionChange(rows) {
      this.selectedRows = rows || [];
    },
    isRowSelectable() {
      return true;
    },
    clearSelection() {
      this.selectedRows = [];
      this.$nextTick(() => {
        if (this.$refs.ordersTable) {
          this.$refs.ordersTable.clearSelection();
        }
      });
    },
    getSelectedIds() {
      return this.selectedRows.map((item) => item.id);
    },
    formatBatchResult(title, data) {
      const lines = [`成功 ${data.success_count} 条，失败 ${data.failed_count} 条`];
      const failed = (data.results || []).filter((item) => !item.success);
      if (failed.length) {
        lines.push("");
        failed.forEach((item) => {
          lines.push(`${item.order_no || item.order_id}：${item.message}`);
        });
      }
      this.$alert(lines.join("\n"), title, {
        confirmButtonText: "确定",
      });
    },
    openCreate() {
      this.editingOrder = null;
      this.createForm = emptyCreateForm();
      this.createForm.source = "manual";
      this.createForm.channel = this.orderSourceOptions[0]?.name || "";
      this.createForm.product_items = [emptyProductItem()];
      if (this.currentUser.role === "sales") {
        this.createForm.sales_id = this.currentUser.id;
      }
      this.createProductOptions = [];
      this.cities = [];
      this.districts = [];
      this.streets = [];
      this.showCreate = true;
    },
    resetCreateDialog() {
      this.editingOrder = null;
      this.createForm = emptyCreateForm();
      this.createProductOptions = [];
      this.cities = [];
      this.districts = [];
      this.streets = [];
    },
    async loadSensitiveValues(orderId, fields) {
      const values = {};
      await Promise.all(
        fields.map(async (field) => {
          try {
            const { data } = await http.get(`/orders/${orderId}/sensitive`, { params: { field } });
            values[field] = data.value || "";
          } catch (error) {
            values[field] = "";
          }
        })
      );
      return values;
    },
    async revealSensitive(row, field) {
      try {
        const { data } = await http.get(`/orders/${row.id}/sensitive`, { params: { field } });
        this.$set(row, field, data.value || "");
      } catch (error) {
        this.$message.error(getErrorMessage(error, "敏感字段加载失败"));
      }
    },
    async openEdit(row) {
      this.editingOrder = row;
      const sensitiveValues = await this.loadSensitiveValues(row.id, [
        "recipient_phone",
        "id_card_no",
        "detail_address",
        "new_phone",
        "card_holder_phone",
      ]);
      this.createForm = {
        ...emptyCreateForm(),
        sales_id: row.sales_id || null,
        product_id: row.product_id || null,
        product_name: row.product_name || "",
        promotion_plan: row.promotion_plan || "",
        recipient_name: row.recipient_name || "",
        recipient_phone: sensitiveValues.recipient_phone || "",
        id_card_no: sensitiveValues.id_card_no || "",
        card_holder_phone: sensitiveValues.card_holder_phone || "",
        province_id: row.province_id || null,
        city_id: row.city_id || null,
        district_id: row.district_id || null,
        street_id: row.street_id || null,
        detail_address: sensitiveValues.detail_address || "",
        city: row.city || "",
        supplier_name: row.supplier_name || "",
        business_type: row.business_type || "",
        team_name: row.team_name || "",
        is_urgent: !!row.is_urgent,
        delivery_sms_enabled: !!row.delivery_sms_enabled,
        is_published: !!row.is_published,
        expected_delivery_date: row.expected_delivery_date || "",
        delivery_time_range: row.delivery_time_range || "",
        receivable_amount: row.receivable_amount,
        is_new_account: !!row.is_new_account,
        new_phone: sensitiveValues.new_phone || "",
        first_recharge_amount: row.first_recharge_amount,
        custom_field1_label: row.custom_field1_label || "",
        custom_field1_value: row.custom_field1_value || "",
        custom_field2_label: row.custom_field2_label || "",
        custom_field2_value: row.custom_field2_value || "",
        remark: row.remark || "",
        source: "manual",
        channel: row.channel || "",
        upstream_order_no: row.upstream_order_no || "",
        upstream_app_id: row.upstream_app_id || null,
        downstream_app_id: row.downstream_app_id || null,
        product_items: this.buildLegacyProductItems({ ...row, new_phone: sensitiveValues.new_phone || row.new_phone || "" }),
      };
      await this.hydrateProductItems(this.createForm.product_items);
      this.syncLegacyProductFields(this.createForm.product_items);
      await this.loadAddressOptionsForForm(row);
      this.showCreate = true;
    },
    exportCurrent() {
      this.openExportDialog("current");
    },
    openExportDialog(mode) {
      if (mode === "batch" && !this.hasSelection) {
        this.$message.warning("请先勾选订单");
        return;
      }
      this.exportMode = mode;
      this.selectedExportFields = this.exportFieldOptions.map((item) => item.key);
      this.showExportFields = true;
    },
    selectAllExportFields() {
      this.selectedExportFields = this.exportFieldOptions.map((item) => item.key);
    },
    clearExportFields() {
      this.selectedExportFields = [];
    },
    async confirmExport() {
      if (!this.selectedExportFields.length) {
        this.$message.warning("请至少选择一个导出字段");
        return;
      }
      this.exportSensitiveMode = "masked";
      this.exportSmsCode = "";
      this.exportVerifyToken = "";
      this.exportPhoneLast4 = "";
      this.showExportFields = false;
      this.showExportSecurity = true;
    },
    async sendExportSmsCode() {
      this.exportSmsSending = true;
      try {
        const { data } = await sendSensitiveExportSms();
        this.exportVerifyToken = data.verify_token;
        this.exportPhoneLast4 = data.phone_last4 || "";
        this.$message.success("验证码已发送");
      } finally {
        this.exportSmsSending = false;
      }
    },
    async submitExport() {
      let sensitiveExportToken = "";
      if (this.exportSensitiveMode === "unmasked") {
        if (!this.exportVerifyToken) {
          this.$message.warning("请先发送短信验证码");
          return;
        }
        if (!this.exportSmsCode) {
          this.$message.warning("请输入短信验证码");
          return;
        }
        const { data } = await verifySensitiveExportSms({ verify_token: this.exportVerifyToken, sms_code: this.exportSmsCode });
        sensitiveExportToken = data.sensitive_export_token;
      }
      this.exporting = true;
      try {
      const fields = [...this.selectedExportFields];
      if (this.exportMode === "batch") {
        const { data } = await batchExportOrders({ order_ids: this.getSelectedIds(), fields }, sensitiveExportToken);
        this.downloadCsv(data, "订单勾选导出.csv");
      } else {
        const { data } = await exportOrders({ ...this.buildListParams(), fields: fields.join(",") }, sensitiveExportToken);
        this.downloadCsv(data, "订单导出.csv");
      }
        this.showExportSecurity = false;
      } finally {
        this.exporting = false;
      }
    },
    downloadCsv(content, fileName) {
      const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(link.href);
    },
    async downloadImportTemplate() {
      const { data } = await downloadOrderImportTemplate();
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "订单导入模板.xlsx";
      link.click();
      URL.revokeObjectURL(link.href);
    },
    triggerImportOrders() {
      if (this.importingOrders) return;
      if (this.$refs.importFileInput) {
        this.$refs.importFileInput.value = "";
        this.$refs.importFileInput.click();
      }
    },
    getImportErrorMessage(error) {
      if (error.response?.status === 504) {
        return "服务器处理订单导入超时。导入可能仍在后台执行，请先不要重复导入，稍后刷新订单列表确认结果；如文件订单较多，请拆分后再导入。";
      }
      if (error.response?.status === 429) {
        return getErrorMessage(error, "已有订单导入任务正在执行，请稍后再试。");
      }
      if (error.code === "ECONNABORTED" || /timeout/i.test(error.message || "")) {
        return "订单导入请求超时，请稍后刷新订单列表确认是否已导入；如数据量较大，请拆分文件后再导入。";
      }
      return getErrorMessage(error, "导入失败");
    },
    async handleImportFileChange(event) {
      if (this.importingOrders) return;
      const file = event.target.files?.[0];
      if (!file) return;
      if (!file.name.toLowerCase().endsWith(".xlsx")) {
        this.$message.warning("请上传 .xlsx 格式文件，不支持 .xls 或 CSV 文件");
        event.target.value = "";
        return;
      }
      this.importingOrders = true;
      try {
        const formData = new FormData();
        formData.append("file", file);
        const { data } = await importOrders(formData);
        this.importResult = data || { success_count: 0, failed_count: 0, results: [] };
        this.showImportResult = true;
        this.loadOrders();
      } catch (error) {
        this.$alert(this.getImportErrorMessage(error), "导入失败", {
          confirmButtonText: "知道了",
          type: "error",
        });
      } finally {
        this.importingOrders = false;
        event.target.value = "";
      }
    },
    handleFileChange(event) {
      const file = event.target.files?.[0] || null;
      this.selectedFile = file;
      if (file && !this.attachmentForm.file_name) {
        this.attachmentForm.file_name = file.name;
      }
    },
    openAssign(row) {
      this.batchAssignMode = false;
      this.selectedOrder = row;
      this.assignForm = { target_type: "courier", courier_id: row.courier_id || null, sales_id: null };
      this.showAssign = true;
    },
    openBatchAssign() {
      if (!this.hasSelection) {
        this.$message.warning("请先勾选订单");
        return;
      }
      this.batchAssignMode = true;
      this.selectedOrder = null;
      this.assignForm = { target_type: "courier", courier_id: null, sales_id: null };
      if (!this.allowAssignSales) {
        this.assignForm.target_type = "courier";
      }
      this.showAssign = true;
    },
    resetAssignDialog() {
      this.batchAssignMode = false;
      this.assignForm = { target_type: "courier", courier_id: null, sales_id: null };
      if (!this.showStatus && !this.showAttachment && !this.showLogs) {
        this.selectedOrder = null;
      }
    },
    openStatus(row) {
      this.batchStatusMode = false;
      this.selectedOrder = row;
      this.statusForm = emptyStatusForm();
      this.statusForm.status = this.statusOptions[0] || "";
      this.showStatus = true;
    },
    openBatchStatus() {
      if (!this.hasSelection) {
        this.$message.warning("请先勾选订单");
        return;
      }
      this.batchStatusMode = true;
      this.selectedOrder = null;
      this.statusForm = emptyStatusForm();
      this.statusForm.status = this.statusOptions[0] || "";
      this.showStatus = true;
    },
    resetStatusDialog() {
      this.batchStatusMode = false;
      this.statusForm = emptyStatusForm();
      if (!this.showAssign && !this.showAttachment && !this.showLogs) {
        this.selectedOrder = null;
      }
    },
    openAttachment(row) {
      this.selectedOrder = row;
      this.attachmentForm = emptyAttachmentForm();
      this.selectedFile = null;
      this.showAttachment = true;
    },
    async openLogs(row) {
      try {
        this.selectedOrder = row;
        const { data } = await fetchAuditLogs({ order_id: row.id, page: 1, page_size: 100 });
        this.logs = data.items || [];
        this.showLogs = true;
      } catch (error) {
        this.$message.error(getErrorMessage(error, "日志加载失败"));
      }
    },
    goDetail(row) {
      this.$router.push(`/orders/${row.id}`);
    },
    formatOrderAddress(row) {
      if (!row) return "-";
      return [row.province_name, row.city_name, row.district_name, row.detail_address].filter(Boolean).join("");
    },
    formatOrderLocation(row) {
      if (!row || row.address_latitude == null || row.address_longitude == null) return "-";
      return `${row.address_latitude}, ${row.address_longitude}`;
    },
    openMap(row) {
      this.selectedMapOrder = row;
      if (row.address_latitude == null || row.address_longitude == null) {
        this.mapUrl = "";
        this.showMapDialog = true;
        return;
      }
      const provider = (this.locationSettings && this.locationSettings.map_provider) || "tencent";
      const key = provider === "amap" ? this.locationSettings.amap_map_key : this.locationSettings.tencent_map_key;
      const title = encodeURIComponent(row.order_no || "订单地址");
      const address = encodeURIComponent(this.formatOrderAddress(row) || "订单地址");
      this.mapUrl = provider === "amap"
        ? `https://m.amap.com/navi/?dest=${row.address_longitude},${row.address_latitude}&destName=${title}&key=${encodeURIComponent(key)}`
        : `https://apis.map.qq.com/tools/poimarker?type=0&marker=coord:${row.address_latitude},${row.address_longitude};title:${title};addr:${address}&key=${encodeURIComponent(key)}&referer=${encodeURIComponent("配送系统")}`;
      this.showMapDialog = true;
    },
    async submitAssign() {
      const payload = this.assignForm.target_type === "sales"
        ? { sales_id: this.assignForm.sales_id, courier_id: null }
        : { courier_id: this.assignForm.courier_id, sales_id: null };
      try {
        if (this.batchAssignMode) {
          const { data } = await batchAssignOrders({ ...payload, order_ids: this.getSelectedIds() });
          this.$message.success("批量派单已处理");
          this.showAssign = false;
          this.formatBatchResult("批量派单结果", data);
          await this.loadOrders();
          this.batchAssignMode = false;
          return;
        }
        await assignOrder(this.selectedOrder.id, payload);
        this.$message.success("派单成功");
        this.showAssign = false;
        this.resetAssignDialog();
        this.loadOrders();
      } catch (error) {
        this.$message.error(getErrorMessage(error, this.batchAssignMode ? "批量派单失败" : "派单失败"));
      }
    },
    async submitStatus() {
      const payload = this.buildStatusPayload();
      if (!payload) {
        return;
      }
      try {
        if (this.batchStatusMode) {
          const { data } = await batchChangeOrdersStatus({ ...payload, order_ids: this.getSelectedIds() });
          this.$message.success("批量改状态已处理");
          this.showStatus = false;
          this.formatBatchResult("批量改状态结果", data);
          await this.loadOrders();
          this.batchStatusMode = false;
          return;
        }
        await changeStatus(this.selectedOrder.id, payload);
        this.$message.success("状态更新成功");
        this.showStatus = false;
        this.resetStatusDialog();
        this.loadOrders();
      } catch (error) {
        this.$message.error(getErrorMessage(error, this.batchStatusMode ? "批量改状态失败" : "状态更新失败"));
      }
    },
    buildStatusPayload() {
      const payload = { ...this.statusForm };
      payload.reschedule_time = payload.status === "改约中" && payload.reschedule_time ? payload.reschedule_time : null;
      if (payload.status !== "待审核") {
        return payload;
      }
      payload.payment_type = (payload.payment_type || "").trim();
      if (!payload.payment_type) {
        this.$message.warning("请选择支付方式");
        return null;
      }
      if (payload.actual_amount === null || payload.actual_amount === undefined || payload.actual_amount === "") {
        this.$message.warning("请填写实收金额");
        return null;
      }
      payload.actual_amount = Number(payload.actual_amount);
      if (Number.isNaN(payload.actual_amount) || payload.actual_amount < 0) {
        this.$message.warning("请填写合法的实收金额");
        return null;
      }
      payload.is_new_account = !!payload.is_new_account;
      if (payload.is_new_account) {
        payload.new_phone = (payload.new_phone || "").trim();
        if (!payload.new_phone) {
          this.$message.warning("请填写新开号码");
          return null;
        }
        if (payload.first_recharge_amount === null || payload.first_recharge_amount === undefined || payload.first_recharge_amount === "") {
          this.$message.warning("请填写首充金额");
          return null;
        }
        payload.first_recharge_amount = Number(payload.first_recharge_amount);
        if (Number.isNaN(payload.first_recharge_amount) || payload.first_recharge_amount < 0) {
          this.$message.warning("请填写合法的首充金额");
          return null;
        }
      } else {
        payload.new_phone = "";
        payload.first_recharge_amount = null;
      }
      return payload;
    },
    async submitBatchUrgent(isUrgent) {
      if (!this.hasSelection) {
        this.$message.warning("请先勾选订单");
        return;
      }
      const actionText = isUrgent ? "标记加急" : "取消加急";
      try {
        await this.$confirm(`确认对当前勾选订单执行“${actionText}”吗？`, "批量操作确认", { type: "warning" });
        const { data } = await batchMarkOrdersUrgent({ order_ids: this.getSelectedIds(), is_urgent: isUrgent });
        this.$message.success(`${actionText}已处理`);
        this.formatBatchResult(`批量${actionText}结果`, data);
        this.loadOrders();
      } catch (error) {
        if (error !== "cancel" && error !== "close") {
          this.$message.error(getErrorMessage(error, `${actionText}失败`));
        }
      }
    },
    async submitBatchPublish() {
      if (!this.hasSelection) {
        this.$message.warning("请先勾选订单");
        return;
      }
      try {
        await this.$confirm("确认发布当前勾选订单吗？", "批量发布确认", { type: "warning" });
        const { data } = await batchPublishOrders({ order_ids: this.getSelectedIds() });
        this.$message.success("批量发布已处理");
        this.formatBatchResult("批量发布结果", data);
        this.loadOrders();
      } catch (error) {
        if (error !== "cancel" && error !== "close") {
          this.$message.error(getErrorMessage(error, "批量发布失败"));
        }
      }
    },
    submitBatchExport() {
      this.openExportDialog("batch");
    },
    async submitAttachment() {
      if (!this.selectedFile) {
        this.$message.warning("请先选择文件");
        return;
      }
      this.uploading = true;
      try {
        const formData = new FormData();
        formData.append("file", this.selectedFile);
        const uploadResp = await uploadFile(formData);
        await uploadAttachment(this.selectedOrder.id, {
          file_name: this.attachmentForm.file_name || uploadResp.data.file_name,
          file_url: uploadResp.data.file_url,
          object_name: uploadResp.data.object_name,
          provider: uploadResp.data.provider,
          file_type: this.attachmentForm.file_type || (uploadResp.data.mime_type && uploadResp.data.mime_type.startsWith("audio/") ? "audio" : "image"),
          mime_type: uploadResp.data.mime_type,
          size: uploadResp.data.size,
          expire_at: this.attachmentForm.expire_at || null,
        });
        this.$message.success("附件上传成功");
        this.showAttachment = false;
        this.loadOrders();
      } catch (error) {
        this.$message.error(getErrorMessage(error, "附件上传失败"));
      } finally {
        this.uploading = false;
      }
    },
    async review(row, approved) {
      try {
        await reviewOrder(row.id, { approved, remark: approved ? "后台审核通过" : "后台驳回，继续配送" });
        this.$message.success(approved ? "审核通过" : "审核驳回");
        this.loadOrders();
      } catch (error) {
        this.$message.error(getErrorMessage(error, approved ? "审核通过失败" : "审核驳回失败"));
      }
    },
    handleMoreAction(command, row) {
      if (command === "review_pass") {
        this.review(row, true);
        return;
      }
      if (command === "review_reject") {
        this.review(row, false);
        return;
      }
      if (command === "attachment") {
        this.openAttachment(row);
        return;
      }
      if (command === "logs") {
        this.openLogs(row);
        return;
      }
      if (command === "delete") {
        if (!this.canDeleteOrder(row)) return;
        this.removeOrder(row);
      }
    },
    async removeOrder(row) {
      if (!this.canDeleteOrder(row)) {
        this.$message.warning("无权删除该订单");
        return;
      }
      try {
        await this.$confirm(`确认逻辑删除订单 ${row.order_no} 吗？`, "删除订单", { type: "warning" });
        await deleteOrder(row.id);
        this.$message.success("订单已删除");
        this.loadOrders();
      } catch (error) {
        if (error !== "cancel" && error !== "close") {
          this.$message.error(getErrorMessage(error, "删除失败"));
        }
      }
    },
    async submitCreate() {
      if (!this.validateCreateForm()) {
        return;
      }
      const province = this.provinces.find((item) => item.id === this.createForm.province_id);
      const city = this.cities.find((item) => item.id === this.createForm.city_id);
      const payload = {
        ...this.createForm,
        product_items: (this.createForm.product_items || []).map((item) => ({
          business_type: item.business_type,
          product_id: item.product_id || null,
          product_name: item.product_name,
          is_new_account: !!item.is_new_account,
          new_phone: item.is_new_account ? item.new_phone : "",
          first_recharge_amount: item.is_new_account ? item.first_recharge_amount : null,
        })),
        channel: this.createForm.channel,
        expected_delivery_date: this.createForm.expected_delivery_date || null,
        delivery_time_range: this.createForm.delivery_time_range,
        city: [province?.name, city?.name].filter(Boolean).join("/"),
      };
      if (this.editingOrder) {
        delete payload.source;
      } else {
        payload.source = "manual";
      }
      if (!this.canUseDeliverySms) {
        delete payload.delivery_sms_enabled;
      }
      try {
        if (this.editingOrder) {
          await updateOrder(this.editingOrder.id, payload);
          this.$message.success("编辑成功");
        } else {
          await createOrder(payload);
          this.$message.success("创建成功");
        }
        this.showCreate = false;
        this.editingOrder = null;
        this.createForm = emptyCreateForm();
          this.loadOrders();
      } catch (error) {
        this.$message.error(getErrorMessage(error, this.editingOrder ? "编辑失败" : "创建失败"));
      }
    },
    canEdit(row) {
      return !!row.can_operate && hasRole(this.currentUser, ["super_admin", "admin", "sales"]) && row.status !== "已完成";
    },
    canAssign(row) {
      return !!row.can_operate && this.canAssignOrders && row.status !== "已完成";
    },
    canChangeStatus(row) {
      const canChangeStatus = row.can_change_status === undefined ? row.can_operate : row.can_change_status;
      return !!canChangeStatus && hasRole(this.currentUser, ["super_admin", "admin", "sales"]);
    },
    canReview(row) {
      return !!row.can_operate && hasRole(this.currentUser, ["super_admin", "admin", "sales"]) && row.status === "待审核";
    },
    canUploadAttachment(row) {
      return !!row.can_operate && hasRole(this.currentUser, ["super_admin", "admin", "sales"]) && row.status !== "已完成";
    },
    canDeleteOrder(row) {
      return !!row.can_delete;
    },
    hasMoreActions(row) {
      return this.canReview(row) || this.canUploadAttachment(row) || this.canDeleteOrder(row);
    },
  },
};
</script>

<style scoped>
.orders-page {
  min-width: 0;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 18px;
}

.header-row h2 {
  margin: 0 0 8px;
}

.header-row p {
  margin: 0;
  color: #60718f;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.filter-card,
.table-card {
  padding: 18px;
}

.filter-card {
  margin-bottom: 18px;
}

.filter-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  color: #35507d;
}

.table-card {
  overflow: hidden;
}

.toolbar-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin: -6px;
}

.toolbar-row > * {
  margin: 6px;
}

.toolbar-row :deep(.el-input),
.toolbar-row :deep(.el-select) {
  width: 220px;
}

.toolbar-row :deep(.el-date-editor--daterange) {
  width: 360px;
}

.toolbar-row :deep(.el-button) {
  width: auto;
}

.delivery-time-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.delivery-time-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.delivery-time-presets :deep(.el-button) {
  margin-left: 0;
}

.product-items-editor {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.product-item-row {
  display: grid;
  grid-template-columns: minmax(130px, 1fr) minmax(150px, 1fr) auto minmax(130px, 1fr) 150px auto;
  gap: 8px;
  align-items: center;
}

.product-item-row :deep(.el-select),
.product-item-row :deep(.el-input),
.product-item-row :deep(.el-input-number) {
  width: 100%;
}

.product-item-row :deep(.el-switch) {
  white-space: nowrap;
}

.product-item-delete {
  grid-column: 6;
  justify-self: end;
}

.product-item-row :deep(.product-item-delete.el-button.is-circle) {
  width: 32px;
  height: 32px;
  padding: 0;
  flex: 0 0 32px;
}

.custom-field-section {
  margin-top: 6px;
  padding: 14px 16px 0;
  border: 1px solid #e8edf4;
  border-radius: 6px;
  background: #fafbfc;
}

.custom-field-section__title {
  margin-bottom: 12px;
  color: #35507d;
  font-size: 14px;
  font-weight: 600;
}

.batch-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #f7f9fc;
}

.batch-toolbar__left {
  color: #5d6b82;
  font-size: 13px;
}

.batch-toolbar__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.table-scroll {
  width: 100%;
  overflow: hidden;
}

.table-scroll :deep(.el-table) {
  width: 100%;
}

.table-scroll :deep(.el-table__body-wrapper) {
  overflow: auto !important;
}

.table-scroll :deep(.el-table__header-wrapper),
.table-scroll :deep(.el-table__footer-wrapper) {
  overflow-x: hidden !important;
}

.table-scroll :deep(.el-table__fixed-right),
.table-scroll :deep(.el-table__fixed-right-patch) {
  background: #ffffff !important;
}

.table-scroll :deep(.el-table__fixed-right::before) {
  background: #ffffff !important;
}

.table-scroll :deep(.el-table__fixed-right th),
.table-scroll :deep(.el-table__fixed-right td) {
  background: #ffffff !important;
}

.table-scroll :deep(.el-table__fixed-right .cell) {
  padding-left: 6px !important;
  padding-right: 6px !important;
  overflow: hidden;
}

.table-scroll :deep(.el-table__fixed-right th .cell) {
  min-width: 100%;
  padding-left: 6px !important;
  padding-right: 6px !important;
  white-space: nowrap;
  overflow: visible !important;
  text-align: center;
}

.table-scroll :deep(.el-table__fixed-right .el-button--mini) {
  margin-left: 0;
  padding: 7px 8px;
}

.table-scroll :deep(.action-column-header .cell) {
  min-width: 100%;
  padding-left: 6px !important;
  padding-right: 6px !important;
  overflow: visible !important;
  white-space: nowrap;
  text-align: center;
}


.table-scroll :deep(.el-table__header),
.table-scroll :deep(.el-table__body),
.table-scroll :deep(.el-table__footer) {
  min-width: 3500px;
}

.pager-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}

.action-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
}

.status-badge,
.timeout-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 64px;
  height: 24px;
  padding: 0 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
}

.status-badge--wait-assign {
  color: #7c4a03;
  background: #fff2cc;
}

.status-badge--wait-delivery {
  color: #1f5f99;
  background: #d9ecff;
}

.status-badge--in-delivery {
  color: #0f766e;
  background: #d1fae5;
}

.status-badge--rescheduled {
  color: #7c3aed;
  background: #ede9fe;
}

.status-badge--pending-review {
  color: #9a3412;
  background: #fed7aa;
}

.status-badge--completed {
  color: #166534;
  background: #dcfce7;
}

.status-badge--failed {
  color: #991b1b;
  background: #fee2e2;
}

.status-badge--default {
  color: #475569;
  background: #e2e8f0;
}

.urgent-badge--yes {
  color: #ffffff;
  background: #dc2626;
}

.urgent-badge--no {
  color: #ffffff;
  background: #16a34a;
}

.sms-badge--yes {
  color: #ffffff;
  background: #2563eb;
}

.sms-badge--no {
  color: #166534;
  background: #dcfce7;
}

.published-badge--yes {
  color: #ffffff;
  background: #16a34a;
}

.published-badge--no {
  color: #ffffff;
  background: #dc2626;
}

.timeout-badge--yes {
  color: #ffffff;
  background: #dc2626;
}

.timeout-badge--no {
  color: #166534;
  background: #dcfce7;
}

.danger-dropdown-item {
  color: #d93025;
}

.timeline-wrap {
  max-height: 520px;
  overflow-y: auto;
  padding-right: 8px;
}

.timeline-card {
  padding: 14px 16px;
  border-radius: 12px;
  background: #f7f9fc;
}

.timeline-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  color: #35507d;
}

.timeline-head strong {
  font-size: 14px;
  color: #173c89;
}

.timeline-head span {
  font-size: 12px;
  color: #60718f;
}

.timeline-role,
.timeline-action {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  border-radius: 4px;
  font-weight: 600;
}

.timeline-role {
  color: #166534;
  background: #dcfce7;
}

.timeline-action {
  color: #1f5f99;
  background: #d9ecff;
}

.timeline-content {
  line-height: 1.7;
  color: #385074;
  word-break: break-all;
}

.map-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}

.map-summary-item {
  padding: 12px 14px;
  border: 1px solid #e8edf4;
  border-radius: 6px;
  background: #fafbfc;
  font-size: 13px;
  color: #34495e;
}

.map-summary-label {
  display: block;
  margin-bottom: 6px;
  color: #8b9bb5;
  font-size: 12px;
}

.map-box {
  width: 100%;
  height: 70vh;
}

.map-frame {
  width: 100%;
  height: 100%;
}

.empty-map {
  padding: 40px 0;
  text-align: center;
  color: #8b9bb5;
}

.export-field-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  color: #5f7498;
}

.export-field-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px 14px;
  max-height: 420px;
  overflow: auto;
}

.export-field-grid :deep(.el-checkbox) {
  margin-right: 0;
  min-width: 0;
}
.export-mode-options { display: flex; flex-direction: column; gap: 14px; }
.sms-verify-row { display: grid; grid-template-columns: 1fr auto; gap: 10px; margin-top: 18px; }
.sms-verify-row p { grid-column: 1 / -1; margin: 0; color: #5f7498; }

.sensitive-cell {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
}

.sensitive-cell > span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sensitive-cell :deep(.el-button) {
  flex: 0 0 auto;
  padding: 0;
}

@media (max-width: 1280px) {
  .header-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .header-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .batch-toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .export-field-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
